/**
 * Navigation utility to determine which menu items a user can access
 * Based on their role and permissions
 */
import React from 'react';

export interface User {
  _id: string;
  username: string;
  email: string;
  role: string;
  isActive: boolean;
  permissions?: {
    forms?: {
      create?: boolean;
      edit?: boolean;
      delete?: boolean;
      view?: boolean;
    };
    submissions?: {
      view?: boolean;
      edit?: boolean;
      approve_payment?: boolean;
      export?: boolean;
    };
    notifications?: {
      send_email?: boolean;
      send_sms?: boolean;
    };
    admin?: {
      manage_users?: boolean;
      manage_settings?: boolean;
      view_analytics?: boolean;
    };
  };
}

export interface MenuItem {
  text: string;
  icon: React.ReactElement;
  path: string;
  requiresPermission?: {
    category: keyof User['permissions'];
    action: string;
  } | null;
  requiresRole?: string[];
}

/**
 * Check if user has specific permission
 */
export function hasPermission(user: User | null, category: string, action: string): boolean {
  if (!user || !user.permissions) return false;
  
  const categoryPerms = user.permissions[category as keyof typeof user.permissions];
  if (!categoryPerms || typeof categoryPerms !== 'object') return false;
  
  return Boolean(categoryPerms[action as keyof typeof categoryPerms]);
}

/**
 * Check if user has required role
 */
export function hasRole(user: User | null, roles: string[]): boolean {
  if (!user) return false;
  return roles.includes(user.role);
}

/**
 * Filter menu items based on user permissions and role
 */
export function getAvailableMenuItems(user: User | null, allMenuItems: MenuItem[]): MenuItem[] {
  if (!user) return [];
  
  return allMenuItems.filter(item => {
    // Check role requirement
    if (item.requiresRole && !hasRole(user, item.requiresRole)) {
      return false;
    }
    
    // Check permission requirement
    if (item.requiresPermission) {
      const { category, action } = item.requiresPermission;
      if (!hasPermission(user, category, action)) {
        return false;
      }
    }
    
    return true;
  });
}

/**
 * Get role-based capabilities summary
 */
export function getRoleCapabilities(role: string): {
  canCreateForms: boolean;
  canEditForms: boolean;
  canDeleteForms: boolean;
  canViewSubmissions: boolean;
  canEditSubmissions: boolean;
  canApprovePayments: boolean;
  canExportData: boolean;
  canSendNotifications: boolean;
  canManageUsers: boolean;
  canManageSettings: boolean;
  canViewAnalytics: boolean;
} {
  const capabilities = {
    canCreateForms: false,
    canEditForms: false,
    canDeleteForms: false,
    canViewSubmissions: false,
    canEditSubmissions: false,
    canApprovePayments: false,
    canExportData: false,
    canSendNotifications: false,
    canManageUsers: false,
    canManageSettings: false,
    canViewAnalytics: false,
  };

  switch (role) {
    case 'super_admin':
      return {
        canCreateForms: true,
        canEditForms: true,
        canDeleteForms: true,
        canViewSubmissions: true,
        canEditSubmissions: true,
        canApprovePayments: true,
        canExportData: true,
        canSendNotifications: true,
        canManageUsers: true,
        canManageSettings: true,
        canViewAnalytics: true,
      };
    
    case 'admin':
      return {
        canCreateForms: true,
        canEditForms: true,
        canDeleteForms: false,
        canViewSubmissions: true,
        canEditSubmissions: true,
        canApprovePayments: true,
        canExportData: true,
        canSendNotifications: true,
        canManageUsers: true,
        canManageSettings: false,
        canViewAnalytics: true,
      };
    
    case 'form_manager':
      return {
        canCreateForms: true,
        canEditForms: true,
        canDeleteForms: false,
        canViewSubmissions: true,
        canEditSubmissions: false,
        canApprovePayments: false,
        canExportData: false,
        canSendNotifications: false,
        canManageUsers: false,
        canManageSettings: false,
        canViewAnalytics: false,
      };
    
    case 'payment_approver':
      return {
        canCreateForms: false,
        canEditForms: false,
        canDeleteForms: false,
        canViewSubmissions: true,
        canEditSubmissions: false,
        canApprovePayments: true,
        canExportData: true,
        canSendNotifications: false,
        canManageUsers: false,
        canManageSettings: false,
        canViewAnalytics: false,
      };
    
    case 'submission_viewer':
      return {
        canCreateForms: false,
        canEditForms: false,
        canDeleteForms: false,
        canViewSubmissions: true,
        canEditSubmissions: false,
        canApprovePayments: false,
        canExportData: false,
        canSendNotifications: false,
        canManageUsers: false,
        canManageSettings: false,
        canViewAnalytics: false,
      };
    
    case 'submission_editor':
      return {
        canCreateForms: false,
        canEditForms: false,
        canDeleteForms: false,
        canViewSubmissions: true,
        canEditSubmissions: true,
        canApprovePayments: false,
        canExportData: true,
        canSendNotifications: false,
        canManageUsers: false,
        canManageSettings: false,
        canViewAnalytics: false,
      };
    
    case 'notification_manager':
      return {
        canCreateForms: false,
        canEditForms: false,
        canDeleteForms: false,
        canViewSubmissions: true,
        canEditSubmissions: false,
        canApprovePayments: false,
        canExportData: false,
        canSendNotifications: true,
        canManageUsers: false,
        canManageSettings: false,
        canViewAnalytics: false,
      };
    
    default:
      return capabilities;
  }
}
